import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useClickAway, useDebounce } from 'react-use'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'

export const useSearch = (
  onChange: (value: string) => void,
  initialValue: string
) => {
  // A report opened on a link that carries a search term shows the term, not an
  // empty box over a filtered list.
  const [showInput, setShowInput] = useState(Boolean(initialValue))
  const [inputValue, setInputValue] = useState(initialValue)

  const searchRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const changed = useRef(false)

  useDebounce(
    () => {
      if (changed.current && inputValue) {
        sendGtmEvent(GtmTriggerName.REPORT_SEARCH_USED)
      }

      changed.current = false
      onChange(inputValue)
    },
    500,
    [inputValue]
  )

  // Clicking elsewhere tidies away an empty box, but a box with a term in it is
  // the only sign of why the list is short, so it stays until the user clears it.
  useClickAway(searchRef, () => {
    if (showInput && !inputValue) {
      setShowInput(false)
    }
  })

  useEffect(() => {
    if (!showInput) {
      return
    }

    inputRef.current?.focus({ preventScroll: true })
  }, [showInput])

  const handleButtonClick = () => {
    setShowInput(true)
  }

  const handleCloseClick = () => {
    setShowInput(false)
    setInputValue('')
  }

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    changed.current = true
    setInputValue(e.target.value)
  }

  return {
    searchRef,
    inputRef,
    showInput,
    inputValue,
    handleButtonClick,
    handleCloseClick,
    handleOnChange,
  }
}
